import { useDisclosure, useToast } from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import {
  organizationQuestionnaireKeys,
  upstreamTransportationAndDistributionKeys,
  useAddUpstreamTransportationAndDistributionItems,
  useDeleteUpstreamTransportationAndDistributionItem,
  useDownloadUpstreamTransportationAndDistributionTemplate,
  useGetTransportTypes,
  useGetUpstreamTransportationAndDistributionItems,
  useImportUpstreamTransportationAndDistributionItems,
  useUpdateUpstreamTransportationAndDistributionItem
} from "../../../../../../hooks/query";
import CategoryForm from "./CategoryForm";
import * as React from "react";
import { useCallback, useState } from "react";
import { showErrorToast, showSuccessToast } from "../../../../../../utils";
import { useDispatch } from "react-redux";
import { useUpdateUpstreamTransportationAndDistributionCategory } from "../../../../../../hooks/query/scope-3";
import { hideAppSpinner, showAppSpinner } from "../../../../../../context/Store/slices/appSpinnerSlice";
import { CategoryGrid } from "../CategoryGrid";
import { DeleteDialog } from "../../../../../DataEntryPlanning/components/DeleteDialog";
import { TransportationAndDistributionModalWrapper } from "../CategoryModalWrappers";
import {
  mapTransportationAndDistributionApiData,
  mapTransportationAndDistributionFormData
} from "./transportationAndDistributionHelpers";
import { buildDimensionFormDefaults } from "./helpers";

function UpstreamTransportationAndDistribution({
                                                 defaultValues,
                                                 handleCategoryChange,
                                                 tabKey,
                                                 finalize,
                                                 finalizeQuestionnaire,
                                                 isPreview,
                                                 organizationStructure,
                                                 dimensionItems
                                               }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const {
    data: transportTypes
  } = useGetTransportTypes({
    onError: err => showErrorToast(toast, err.message)
  });

  const {
    id: questionnaireId
  } = defaultValues;
  const { t } = useTranslation("translation", {
    keyPrefix: "organizationQuestionnaire.scope3"
  });
  const { t: globalTranslation } = useTranslation("translation");
  const commonKey = "inputs.transportationAndDistribution";

  const {
    isOpen: isCategoryModalOpen,
    onOpen: onCategoryModalOpen,
    onClose: onCategoryModalClose
  } = useDisclosure();

  const {
    isOpen: isDeleteItemDialogOpen,
    onOpen: onDeleteItemDialogOpen,
    onClose: onDeleteItemDialogClose
  } = useDisclosure();

  const [selectedItem, setSelectedItem] = useState(null);
  const [categoryModalData, setCategoryModalData] = useState(null);

  const colHeaders = [
    t(`${commonKey}.typeOfTransport.name`),
    t(`${commonKey}.subtypeOfTransport.name`),
    t(`${commonKey}.subtypeTwo.name`),
    t(`${commonKey}.unit.name`),
    t(`${commonKey}.value`)
  ];
  const attributesOrder = ["typeOfTransport", "subtypeOfTransport", "subtypeTwo", "unit", "value"];
  const FORM_INPUT_KEY = "upstreamTransportationAndDistribution";

  const {
    handleSubmit,
    formState: { isDirty }
  } = useForm();

  const queryConfig = {
    onSuccess: () => {
      showSuccessToast(toast, "Category Upstream Transportation and Distribution saved!");
      if (finalize) {
        finalizeQuestionnaire(questionnaireId);
      } else {
        queryClient.invalidateQueries(organizationQuestionnaireKeys.orgQuestionnaireById());
        if (!isLoadingOnUpdate) {
          handleCategoryChange();
        }
      }
    },
    onError: err => showErrorToast(toast, err.message)
  };

  const {
    mutate: update,
    isLoading: isLoadingOnUpdate
  } = useUpdateUpstreamTransportationAndDistributionCategory(queryConfig);

  const formId = `formStep${tabKey}`;

  const onSubmit = (() => {
    if (!isPreview) {
      if (isDirty) {
        update({ questionnaireId, data: {} });
      } else {
        finalize ? finalizeQuestionnaire(questionnaireId) : handleCategoryChange();
      }
    } else {
      handleCategoryChange();
    }
  });

  const prepareSectionValues = useCallback(items => {
    const typeOfTransportKey = `${commonKey}.typeOfTransport.options`;
    const subtypeOfTransportKey = `${commonKey}.subtypeOfTransport.options`;
    const subtypeTwoKey = `${commonKey}.subtypeTwo.options`;
    const unitKey = `${commonKey}.unit.options`;

    // mapTransportationAndDistributionApiData doesn't pass through dimensionItems,
    // so we zip them back in here from the raw items.
    const mapped = mapTransportationAndDistributionApiData(items, transportTypes);
    return mapped.map((item, idx) => {
      const rawItem = items[idx];
      return {
        id: item.id ?? null,
        dimensionItems: rawItem?.dimensionItems ?? null,
        dimensionOneItem: rawItem?.dimensionItems?.dimensionOneItem ?? null,
        dimensionTwoItem: rawItem?.dimensionItems?.dimensionTwoItem ?? null,
        dimensionThreeItem: rawItem?.dimensionItems?.dimensionThreeItem ?? null,
        dimensionFourItem: rawItem?.dimensionItems?.dimensionFourItem ?? null,
        dimensionFiveItem: rawItem?.dimensionItems?.dimensionFiveItem ?? null,
        typeOfTransportValue: item.typeOfTransport,
        typeOfTransport: t(`${typeOfTransportKey}.${item.typeOfTransport}`, { defaultValue: item.typeOfTransport ?? "" }),
        subtypeOfTransportValue: item.subtypeOfTransport,
        subtypeOfTransport: t(`${subtypeOfTransportKey}.${item.subtypeOfTransport}`,
          { defaultValue: item.subtypeOfTransport ?? "" }),
        subtypeTwoValue: item.subtypeTwo,
        subtypeTwo: t(`${subtypeTwoKey}.${item.subtypeTwo}`, { defaultValue: item.subtypeTwo ?? "" }),
        unitValue: item.unit,
        unit: t(`${unitKey}.${item.unit}`, { defaultValue: item.unit ?? "" }),
        value: item.value ?? ""
      };
    });
  }, [t, transportTypes]);

  const handleAddItem = () => {
    setCategoryModalData({
      fieldArrayName: FORM_INPUT_KEY,
      columnHeaders: [...colHeaders, ""],
      modalHeaderItemsType: t(`inputs.upstreamTransportationAndDistribution.itemsType`),
      organizationStructure,
      dimensionItems
    });
    onCategoryModalOpen();
  };

  const handleEditItem = (item) => {
    setCategoryModalData({
      fieldArrayName: FORM_INPUT_KEY,
      editData: {
        id: item.id,
        dimensionItems: item.dimensionItems ?? null,
        typeOfTransport: item.typeOfTransportValue,
        subtypeOfTransport: item.subtypeOfTransportValue,
        subtypeTwo: item.subtypeTwoValue,
        unit: item.unitValue,
        value: item.value
      },
      columnHeaders: colHeaders,
      modalHeaderItemsType: t(`inputs.upstreamTransportationAndDistribution.itemsType`),
      organizationStructure,
      dimensionItems
    });
    onCategoryModalOpen();
  };

  const handleDeleteItem = (item) => {
    onDeleteItemDialogOpen();
    setSelectedItem({ id: item.id });
  };

  const handleDeleteItemDialogClose = () => {
    setSelectedItem(null);
    onDeleteItemDialogClose();
  };

  const deleteRecord = () => {
    dispatch(showAppSpinner());
    deleteItem({ questionnaireId, recordId: selectedItem.id });
  };

  const handleCategoryModalClose = () => {
    setCategoryModalData(null);
    onCategoryModalClose();
  };

  const { mutate: addItem } = useAddUpstreamTransportationAndDistributionItems({
    onSuccess: () => {
      handleCategoryModalClose();
      showSuccessToast(toast, globalTranslation("organizationQuestionnaire.page.added"));
      queryClient.invalidateQueries(upstreamTransportationAndDistributionKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const { mutate: updateItem } = useUpdateUpstreamTransportationAndDistributionItem({
    onSuccess: () => {
      handleCategoryModalClose();
      showSuccessToast(toast, globalTranslation("organizationQuestionnaire.page.updated"));
      queryClient.invalidateQueries(upstreamTransportationAndDistributionKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const { mutate: deleteItem } = useDeleteUpstreamTransportationAndDistributionItem({
    onSuccess: () => {
      handleDeleteItemDialogClose();
      showSuccessToast(toast, globalTranslation("organizationQuestionnaire.page.deleted"));
      queryClient.invalidateQueries(upstreamTransportationAndDistributionKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const { mutate: downloadTemplate } = useDownloadUpstreamTransportationAndDistributionTemplate({
    onMutate: () => dispatch(showAppSpinner()),
    onError: err => {
      const parsed = err.response?.data
        ? JSON.parse(String.fromCharCode.apply(null, new Int8Array(err.response.data)))
        : null;
      showErrorToast(toast, parsed?.message ?? err.message);
    },
    onSettled: () => dispatch(hideAppSpinner())
  });

  const { mutate: importItems } = useImportUpstreamTransportationAndDistributionItems({
    onSuccess: () => {
      showSuccessToast(toast, globalTranslation("organizationQuestionnaire.page.imported"));
      queryClient.invalidateQueries(upstreamTransportationAndDistributionKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const handleImport = (e) => {
    if (!e.target.files?.[0]) return;
    const formData = new FormData();
    formData.append("file", e.target.files[0]);
    dispatch(showAppSpinner());
    importItems({ questionnaireId, data: formData });
  };

  const handleCategoryModalSubmit = (formData) => {
    const apiData = mapTransportationAndDistributionFormData(
      formData[categoryModalData.fieldArrayName], transportTypes
    );

    dispatch(showAppSpinner());
    if (categoryModalData.editData) {
      updateItem({
        questionnaireId,
        recordId: apiData[0].id,
        data: apiData[0]
      });
    } else {
      addItem({
        questionnaireId,
        data: apiData
      });
    }
  };

  const getEmptyRow = () => {
    return {
      typeOfTransport: "",
      subtypeOfTransport: "",
      subtypeTwo: "",
      unit: "",
      value: "",
      ...buildDimensionFormDefaults(organizationStructure, dimensionItems)
    };
  };

  return (
    <CategoryForm onSubmit={handleSubmit(onSubmit)} id={formId} marginTop={4}>
      {transportTypes && <CategoryGrid
        questionnaireId={questionnaireId}
        columnHeaders={colHeaders}
        attributesOrder={attributesOrder}
        prepareSectionValues={prepareSectionValues}
        handleAddItem={handleAddItem}
        handleEditItem={handleEditItem}
        handleDeleteItem={handleDeleteItem}
        getItemsFunction={useGetUpstreamTransportationAndDistributionItems}
        isPreview={isPreview}
        maxHeight={"165"}
        organizationStructure={organizationStructure}
        onDownloadTemplate={() => downloadTemplate({ questionnaireId })}
        onImport={handleImport}
        importBtnId={`import-${tabKey}-btn`}
        totalsConfig={{ valueKey: "value", unitKey: "unit" }}
      />}

      <DeleteDialog
        isOpen={isDeleteItemDialogOpen}
        onClose={handleDeleteItemDialogClose}
        onDelete={deleteRecord}
        entityName={"Item"}
      />
      {
        isCategoryModalOpen && categoryModalData !== null && transportTypes && (
          <TransportationAndDistributionModalWrapper
            tabKey={"transportationAndDistribution"}
            modalData={categoryModalData}
            attributesOrder={attributesOrder}
            isOpen={isCategoryModalOpen}
            onClose={handleCategoryModalClose}
            onSubmit={handleCategoryModalSubmit}
            getEmptyRow={getEmptyRow}
            transportTypes={transportTypes}
          />
        )
      }
    </CategoryForm>
  );
}

export default UpstreamTransportationAndDistribution;
